
import { UseQueryResult, QueryFunction, UseQueryOptions, useQuery, QueryKey, useQueryClient } from "react-query";
import { AxiosError } from "@eco/client";
import { useMonitorQuery, UseMonitorQueryConfig } from "./useMonitorQuery";
import { useCallback } from "react";
import { useTranslations } from "hooks";

/**
 * Extends of useQuery that has a generic error and success handling
 */
const useEcoQuery = <TData=Record<string, any>, TError=AxiosError>(
    queryKey: QueryKey,
    fn?: QueryFunction<TData>,
    config?: UseEcoQueryOptions<unknown, TData, TError>) : UseEcoQuery<TData, TError>  =>  {
    
    const [
        ERROR_MESSAGE,
        SERVER_UNREACHABLE
    ] = useTranslations(i18n);

    const queryInstance = useQuery(queryKey, fn!, { enabled: fn !== undefined, ...config });
    const queryClient = useQueryClient()
    useMonitorQuery<TData, TError>(queryInstance, {
        errorMessage: !config?.hideErrorMessage ? ERROR_MESSAGE : undefined,
        onErrorActionClick: () => queryInstance.refetch(),
        transformErrorMessage: (e?: any) => {
            if (e?.response?.status === 0) {
                return SERVER_UNREACHABLE
            }
            return ERROR_MESSAGE
        },
        ...config,
    }, queryKey)

    const invalidateQuery = useCallback(() => {
        queryClient.invalidateQueries(queryKey)
    }, [queryKey])

    const removeQuery = useCallback(() => {
        queryClient.removeQueries(queryKey)
    }, [queryKey])

    const resetQuery = useCallback(() => {
        queryClient.resetQueries(queryKey)
    }, [queryKey])

    return {
        ...queryInstance,
        invalidateQuery,
        removeQuery,
        resetQuery,
    }
};

const i18n = [
    'SDS1Click.Common.ErrorOccured.error',
    'SDS1Click.Common.ServerUnreachable.error'
]

export type UseEcoQuery<TData, TError> = UseQueryResult<TData, TError> & {
    invalidateQuery: () => void
    removeQuery: () => void
    resetQuery: () => void
}

export type UseEcoQueryOptions<TQueryFnData = unknown, TData = unknown, TError = unknown> = 
    UseQueryOptions<TQueryFnData, TError, TData> 
    & UseMonitorQueryConfig<TData, TError>

export default useEcoQuery ;
